import { z } from 'zod';

const envSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  API_BASE_URL: z.string().url().optional(),
  NEXT_PUBLIC_API_BASE_URL: z.string().url().optional(),
  KW_WEB_DOMAIN: z.string().url().optional(),
  KW_GOOGLE_CLIENT_ID: z.string().min(1).optional(),
  KW_JOBS_PAGE_URL: z.string().url().optional(),
  KW_CAREER_COACHING_PAGE_URL: z.string().url().optional(),
  KW_PRO_EXAMPLES_URL: z.string().url().optional(),
  KW_GCS_STORAGE_URL: z.string().url().optional(),
  KW_POSTHOG_KEY: z.string().optional(),
  KW_PUSHER_KEY: z.string().optional(),
  KW_RAZORPAY_KEY: z.string().optional(),
  KW_PADDLE_TOKEN: z.string().optional(),
  KW_DODO_CHECKOUT_MODE: z.enum(['test', 'live']).optional(),
  KW_ALLOW_AI_MODEL_SELECTION: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  KW_WHATSAPP_VERIFICATION_GEO_GATE: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  KW_ADSENSE_ENABLED: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  KW_ADSENSE_CLIENT_ID: z.string().optional(),
  /** GA4 / Google Ads / Facebook / Bing / Clarity — live only; also requires live hostname. */
  KW_ENABLE_MARKETING_TAGS: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  /**
   * Master switch for upgrade-conversion PostHog experiments.
   * Default off: all getExperimentVariant calls return control without hitting PostHog.
   */
  KW_UPGRADE_EXPERIMENTS_ENABLED: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  /**
   * Optional comma-separated PostHog flag keys allowed when the master is on.
   * Empty = all registered upgrade experiment flags are allowed.
   */
  KW_UPGRADE_EXPERIMENT_FLAGS: z.string().optional(),
});

export type ServerEnv = z.infer<typeof envSchema>;

let cachedEnv: ServerEnv | null = null;

export function getServerEnv(): ServerEnv {
  if (cachedEnv) {
    return cachedEnv;
  }

  const parsed = envSchema.safeParse(process.env);
  if (!parsed.success) {
    throw new Error(`Invalid environment configuration: ${parsed.error.message}`);
  }

  if (parsed.data.NODE_ENV === 'production' && !parsed.data.API_BASE_URL) {
    throw new Error('API_BASE_URL is required in production');
  }

  cachedEnv = parsed.data;
  return cachedEnv;
}

export function resetServerEnvCache(): void {
  cachedEnv = null;
}
